import type { ISubAgentNotice } from '@/lib/types/chat';

/**
 * The message that brings a background sub-agent's result into the chat
 * (KAN-1872), read off its metadata.
 *
 * The backend stores it as a question from no one (`sender: 'user'`, no
 * author): to the chat's AI it is the next turn, which it answers on its own.
 * The chat shows nothing of it (its `content` is written for that AI); the
 * result is in the sub-agent's own entry. It stays in the thread all the same,
 * as the question the AI's answer hangs below, and it says that its run is
 * over and, for a failed one, why.
 */

const NOTIFICATION = 'subagent_finished';

const OUTCOMES: readonly ISubAgentNotice['outcome'][] = ['done', 'failed', 'stopped'];

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** An unknown outcome reads as failed, never as done. */
const outcomeOf = (value: unknown): ISubAgentNotice['outcome'] =>
  OUTCOMES.find((outcome) => outcome === value) ?? 'failed';

/** A stop is said by the outcome itself; only a failure carries a code worth reading. */
const errorCodeOf = (value: unknown): ISubAgentNotice['errorCode'] =>
  value === 'restart' || value === 'failed' ? value : null;

/**
 * The notice a message's metadata describes, or `null` for any other message.
 * Also reads the slim copy of that metadata the external share hands out.
 */
export function subAgentNoticeOf(
  meta: Record<string, unknown> | null | undefined
): ISubAgentNotice | null {
  if (!meta || meta['system_notification'] !== NOTIFICATION) return null;
  return {
    callId: text(meta['subagent_call_id']) || null,
    outcome: outcomeOf(meta['subagent_outcome']),
    errorCode: errorCodeOf(meta['subagent_error_code']),
    error: text(meta['subagent_error']) || null,
  };
}

/** The notice a thread message carries (`to-thread-message.ts` puts it into its custom metadata). */
export const subAgentNoticeIn = (custom: unknown): ISubAgentNotice | undefined =>
  (custom as { subagentNotice?: ISubAgentNotice } | undefined)?.subagentNotice;

/**
 * The notice among `messages` of the run its tool call `callId` started;
 * `undefined` while there is none. It is stored once the run is over, so it
 * says so even where the run's own end never arrived.
 */
export function noticeOfRun(
  messages: readonly { metadata?: { custom?: unknown } }[] | undefined,
  callId: string | undefined
): ISubAgentNotice | undefined {
  if (!messages || !callId) return undefined;
  for (const message of messages) {
    const notice = subAgentNoticeIn(message.metadata?.custom);
    if (notice?.callId === callId) return notice;
  }
  return undefined;
}
